import { useCallback, useMemo, useRef } from 'react';

import {
  type OnChangeFn,
  type VisibilityState,
  functionalUpdate,
} from '@tanstack/react-table';

import { useLocalStorage } from '@spotify/confidence-core-react';

const EMPTY_VISIBILITY: VisibilityState = {};

export type DataTableColumnVisibility = {
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
};

export const useDataTableColumnVisibility = ({
  storageKey,
  defaultVisibility = EMPTY_VISIBILITY,
  migrateKey = false,
}: {
  storageKey: string;
  defaultVisibility?: VisibilityState;
  migrateKey?: boolean;
}): DataTableColumnVisibility => {
  const [storedVisibility, setStoredVisibility] =
    useLocalStorage<VisibilityState>(storageKey, {}, { migrateKey });
  const defaultVisibilityRef = useRef(defaultVisibility);
  defaultVisibilityRef.current = defaultVisibility;
  const onColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>(
    updater =>
      setStoredVisibility(current =>
        functionalUpdate(updater, {
          ...defaultVisibilityRef.current,
          ...current,
        }),
      ),
    [setStoredVisibility],
  );
  const columnVisibility = useMemo<VisibilityState>(
    () => ({ ...defaultVisibility, ...storedVisibility }),
    [defaultVisibility, storedVisibility],
  );
  return { columnVisibility, onColumnVisibilityChange };
};
